import { useState, useEffect, useRef, useCallback } from 'react';
import { EXIT_PIN, EXIT_CORNER, EXIT_TAPS, EXIT_WINDOW_MS, EXIT_ZONE_PX } from '../../config/kiosk';
import { useAppContext } from '../../context/AppContext';

// --- helpers de pantalla completa (con prefijos de navegador) ---
const isFullscreen = () => !!(
    document.fullscreenElement || document.webkitFullscreenElement ||
    document.mozFullScreenElement || document.msFullscreenElement
);
const enterFullscreen = () => {
    const el = document.documentElement;
    const fn = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (fn) { try { fn.call(el); } catch (e) {} }
};
const exitFullscreen = () => {
    const fn = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
    if (fn) { try { fn.call(document); } catch (e) {} }
};

// ¿el toque cae en la esquina secreta?
const inCorner = (x, y) => {
    const w = window.innerWidth, h = window.innerHeight, z = EXIT_ZONE_PX;
    switch (EXIT_CORNER) {
        case 'top-right':    return x >= w - z && y <= z;
        case 'bottom-left':  return x <= z && y >= h - z;
        case 'bottom-right': return x >= w - z && y >= h - z;
        case 'top-left':
        default:             return x <= z && y <= z;
    }
};

const KioskExitGate = () => {
    const { sessionData } = useAppContext();
    // PIN para salir = el de la sesión abierta; si no hay, el fijo de config.
    const exitPin = String(sessionData?.pin || EXIT_PIN);
    const [showPin, setShowPin] = useState(false);
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const taps = useRef([]);
    const showPinRef = useRef(false);
    useEffect(() => { showPinRef.current = showPin; }, [showPin]);

    // Detectar N toques en la esquina secreta
    useEffect(() => {
        const onDown = (e) => {
            if (showPinRef.current) return; // ya está abierto el PIN
            const x = e.clientX, y = e.clientY;
            if (x == null || y == null) return;
            if (!inCorner(x, y)) return;
            const now = Date.now();
            taps.current = taps.current.filter(t => now - t <= EXIT_WINDOW_MS);
            taps.current.push(now);
            if (taps.current.length >= EXIT_TAPS) {
                taps.current = [];
                if (isFullscreen()) {
                    // en pantalla completa: pedir PIN para salir
                    setPin(''); setError(''); setShowPin(true);
                } else {
                    // no está en pantalla completa: entrar (sin PIN, es solo activar el tótem)
                    enterFullscreen();
                }
            }
        };
        document.addEventListener('pointerdown', onDown, true);
        return () => document.removeEventListener('pointerdown', onDown, true);
    }, []);

    const close = useCallback(() => { setShowPin(false); setPin(''); setError(''); }, []);

    const validate = useCallback((value) => {
        if (value === exitPin) {
            window.__kioskIntentionalExit = true; // autoriza la salida (no re-entrar en el próximo toque)
            exitFullscreen();  // salir → el botón Salir aparece solo (FloatingLogoutButton)
            close();
        } else {
            setError('PIN incorrecto');
            setPin('');
        }
    }, [close, exitPin]);

    const pressDigit = (d) => {
        setError('');
        setPin(prev => {
            if (prev.length >= exitPin.length) return prev;
            const next = prev + d;
            if (next.length === exitPin.length) {
                setTimeout(() => validate(next), 120); // deja ver el último dígito
            }
            return next;
        });
    };
    const del = () => { setError(''); setPin(prev => prev.slice(0, -1)); };

    if (!showPin) return null;

    const dot = (filled) => (
        <div style={{
            width: 18, height: 18, borderRadius: '50%',
            background: filled ? '#009542' : 'transparent',
            border: '2px solid #009542'
        }} />
    );
    const padBtn = (label, onClick, extra = {}) => (
        <button key={String(label)} onClick={onClick} style={{
            fontSize: '2rem', fontWeight: 600, color: '#009542',
            background: '#F3EFE6', border: '1px solid #E5DFD2', borderRadius: 16,
            padding: '1.1rem 0', cursor: 'pointer', ...extra
        }}>{label}</button>
    );

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 2147483647,
            background: 'rgba(0,0,0,0.85)', display: 'flex',
            alignItems: 'center', justifyContent: 'center'
        }}>
            <div style={{
                background: '#fff', borderRadius: 24, padding: '2rem',
                width: 'min(92vw, 360px)', textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
            }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#009542', margin: '0 0 0.25rem' }}>
                    Salir del tótem
                </h2>
                <p style={{ color: '#6b6455', margin: '0 0 1.25rem', fontSize: '0.95rem' }}>
                    Ingresa el PIN para salir de pantalla completa
                </p>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 14, margin: '0 0 0.5rem', minHeight: 22 }}>
                    {Array.from({ length: exitPin.length }).map((_, i) => (
                        <span key={i}>{dot(i < pin.length)}</span>
                    ))}
                </div>
                <div style={{ color: '#e1251b', minHeight: 22, fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                    {error}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => padBtn(n, () => pressDigit(String(n))))}
                    {padBtn('⌫', del, { background: '#fff', border: '1px solid #E5DFD2' })}
                    {padBtn(0, () => pressDigit('0'))}
                    {padBtn('✕', close, { background: '#fff', border: '1px solid #E5DFD2', color: '#e1251b' })}
                </div>
            </div>
        </div>
    );
};

export default KioskExitGate;
